import useSWR from 'swr';

import { parseErrorResponse } from './problem';

export interface ServiceAccount {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  created_by: string;
  created_at: string;
  token_count: number;
}

export interface SAToken {
  id: string;
  name: string;
  scopes: string[];
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
}

export interface CreatedSAToken extends SAToken {
  token: string;
}

const fetcher = async (url: string) => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
};

export function useServiceAccounts(projectId: string | undefined) {
  return useSWR<ServiceAccount[]>(
    projectId ? `/api/v1/projects/${projectId}/service_accounts` : null,
    fetcher,
  );
}

export function useSATokens(projectId: string | undefined, saId: string | undefined) {
  return useSWR<SAToken[]>(
    projectId && saId ? `/api/v1/projects/${projectId}/service_accounts/${saId}/tokens` : null,
    fetcher,
  );
}

export async function createSA(projectId: string, input: {
  name: string;
  description?: string;
}): Promise<{ id: string }> {
  const res = await fetch(`/api/v1/projects/${projectId}/service_accounts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

export async function deleteSA(projectId: string, saId: string): Promise<void> {
  const res = await fetch(`/api/v1/projects/${projectId}/service_accounts/${saId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

export async function createSAToken(projectId: string, saId: string, input: {
  name: string;
  scopes: string[];
  expiresIn?: string;
}): Promise<CreatedSAToken> {
  const res = await fetch(
    `/api/v1/projects/${projectId}/service_accounts/${saId}/tokens`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}

export async function deleteSAToken(projectId: string, saId: string, tokenId: string): Promise<void> {
  const res = await fetch(
    `/api/v1/projects/${projectId}/service_accounts/${saId}/tokens/${tokenId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

export async function rotateSAToken(projectId: string, saId: string, tokenId: string, input?: {
  overlapIn?: string;
  expiresIn?: string;
}): Promise<CreatedSAToken> {
  const res = await fetch(
    `/api/v1/projects/${projectId}/service_accounts/${saId}/tokens/${tokenId}/rotate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input ?? {}),
    },
  );
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}
